import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { UserPlus, Trash2, Shield, User, Crown, Users, Settings } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamManagement = () => {
  const { user, isSuperAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [newMember, setNewMember] = useState({
    email: '',
    name: '',
    role: 'admin',
    assigned_clients: []
  });

  useEffect(() => {
    fetchMembers();
    fetchClients();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admins`);
      setMembers(response.data.admins);
    } catch (error) {
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);

    try {
      const response = await axios.post(`${API_URL}/admins/invite`, {
        email: newMember.email,
        name: newMember.name,
        role: newMember.role,
        assigned_clients: newMember.role === 'admin' ? newMember.assigned_clients : []
      });
      
      setMembers([...members, response.data.user]);
      setIsDialogOpen(false);
      setNewMember({ email: '', name: '', role: 'admin', assigned_clients: [] });
      toast.success(
        <div>
          <p className="font-semibold">Team member invited!</p>
          <p className="text-sm mt-1">Credentials sent to {response.data.user.email}</p>
        </div>
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId) => {
    try {
      await axios.delete(`${API_URL}/admins/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success('Team member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleUpdateClients = async () => {
    if (!selectedAdmin) return;
    
    try {
      await axios.put(`${API_URL}/admins/${selectedAdmin.id}/clients`, selectedClients);
      setMembers(members.map(m => 
        m.id === selectedAdmin.id 
          ? { ...m, assigned_clients: selectedClients }
          : m
      ));
      setIsClientDialogOpen(false);
      toast.success('Client access updated');
    } catch (error) {
      toast.error('Failed to update client access');
    }
  };

  const openClientDialog = (admin) => {
    setSelectedAdmin(admin);
    setSelectedClients(admin.assigned_clients || []);
    setIsClientDialogOpen(true);
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              SuperAdmin Access Required
            </h2>
            <p className="text-slate-500">
              Only SuperAdmins can manage team members. Contact your administrator for access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const superAdmins = members.filter(m => m.role === 'superadmin');
  const admins = members.filter(m => m.role === 'admin');

  return (
    <div className="space-y-6" data-testid="team-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Team Management</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Manage SuperAdmins and Admins</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#556B2F] hover:bg-[#3d4f22] text-white w-full sm:w-auto" data-testid="invite-member-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Add a new SuperAdmin or Admin to your team. They'll receive login credentials via email.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  placeholder="John Smith"
                  required
                  data-testid="member-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  placeholder="john@agency.com"
                  required
                  data-testid="member-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v, assigned_clients: v === 'superadmin' ? [] : newMember.assigned_clients })}>
                  <SelectTrigger data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Admin (Limited Access)
                      </div>
                    </SelectItem>
                    <SelectItem value="superadmin">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4" />
                        SuperAdmin (Full Access)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {newMember.role === 'superadmin' 
                    ? 'SuperAdmins have full access to all clients and can manage team members'
                    : 'Admins only see clients assigned to them'}
                </p>
              </div>

              {newMember.role === 'admin' && clients.length > 0 && (
                <div className="space-y-2">
                  <Label>Assign Clients (Optional)</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-3 space-y-2">
                    {clients.map(client => (
                      <label key={client.id} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={newMember.assigned_clients.includes(client.id)}
                          onCheckedChange={(checked) => {
                            setNewMember({
                              ...newMember,
                              assigned_clients: checked
                                ? [...newMember.assigned_clients, client.id]
                                : newMember.assigned_clients.filter(id => id !== client.id)
                            });
                          }}
                        />
                        <span className="text-sm">{client.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={inviting} className="flex-1 bg-[#556B2F] hover:bg-[#3d4f22] text-white" data-testid="send-invite-btn">
                  {inviting ? 'Sending...' : 'Send Invite'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* SuperAdmins */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            SuperAdmins ({superAdmins.length})
          </CardTitle>
          <CardDescription>Full access to all clients and team management</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {superAdmins.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-gradient-to-r from-amber-50/50 to-transparent dark:from-amber-900/10"
                data-testid={`member-${member.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-semibold">
                    {member.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-slate-900 dark:text-white">{member.name}</h3>
                      <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <Crown className="w-3 h-3 mr-1" />
                        SuperAdmin
                      </Badge>
                      {member.id === user?.id && (
                        <Badge variant="outline">You</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{member.email}</p>
                  </div>
                </div>

                {member.id !== user?.id && superAdmins.length > 1 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove SuperAdmin?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove {member.name}? They will lose all admin access.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleRemove(member.id)} className="bg-red-600 hover:bg-red-700">
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Admins */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#556B2F]" />
            Admins ({admins.length})
          </CardTitle>
          <CardDescription>Access limited to assigned clients only</CardDescription>
        </CardHeader>
        <CardContent>
          {admins.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No admins yet. Invite team members to help manage clients.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {admins.map((member) => (
                <div
                  key={member.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border border-slate-100 dark:border-slate-800"
                  data-testid={`member-${member.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold">
                      {member.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900 dark:text-white">{member.name}</h3>
                        <Badge className="bg-[#d8e5b8] text-[#3d4f22] dark:bg-[#556B2F]/15 dark:text-[#6B8E23]">
                          <User className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">{member.email}</p>
                      {member.assigned_clients?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-1">
                          {member.assigned_clients.length} client{member.assigned_clients.length > 1 ? 's' : ''} assigned
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openClientDialog(member)}
                      className="gap-1"
                    >
                      <Settings className="w-3 h-3" />
                      Clients
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove Admin?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove {member.name}? They will lose access to all assigned clients.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleRemove(member.id)} className="bg-red-600 hover:bg-red-700">
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Assignment Dialog */}
      <Dialog open={isClientDialogOpen} onOpenChange={setIsClientDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Client Access</DialogTitle>
            <DialogDescription>
              Select which clients {selectedAdmin?.name} can access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {clients.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No clients available</p>
            ) : (
              <div className="max-h-64 overflow-y-auto border rounded-lg p-3 space-y-2">
                {clients.map(client => (
                  <label key={client.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer">
                    <Checkbox
                      checked={selectedClients.includes(client.id)}
                      onCheckedChange={() => toggleClientSelection(client.id)}
                    />
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white text-sm font-semibold">
                      {client.name?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{client.name}</p>
                      <p className="text-xs text-slate-500">{client.industry || 'No industry'}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsClientDialogOpen(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateClients}
                className="flex-1 bg-[#556B2F] hover:bg-[#3d4f22] text-white"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
